// FILE: /src/Components/Apps/CaptionGeneratorApp.js

import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Empty, Modal, Spin, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  HistoryOutlined,
  MessageOutlined,
  ScissorOutlined,
} from "@ant-design/icons";
import { fetchAssets } from "../../store/Slices/librarySlice";
import { getOnboardingForGroup } from "../../services/group";
import {
  fetchAppsCatalog,
  runCaptionGenerator,
  runCaptionVariants,
} from "../../services/apps";
import CaptionOptionsPanel from "./CaptionOptionsPanel";
import CaptionMediaSelector from "./CaptionMediaSelector";
import CaptionResultCard from "./CaptionResultCard";

const DEFAULT_OPTIONS = {
  useBrandKit: true,
  platform: "Instagram",
  tone: "premium",
  emoji_density: "medium",
  caption_length: "short",
  language: "English",
  generate_hashtags: true,
  n: 2,
};

const getCaptionText = (caption) => {
  if (!caption) return "";

  if (typeof caption === "string") {
    return caption;
  }

  if (typeof caption.caption === "string") {
    return caption.caption;
  }

  if (typeof caption.text === "string") {
    return caption.text;
  }

  if (typeof caption.content === "string") {
    return caption.content;
  }

  if (typeof caption.generated_text === "string") {
    return caption.generated_text;
  }

  if (typeof caption.output === "string") {
    return caption.output;
  }

  if (typeof caption.raw?.caption === "string") {
    return caption.raw.caption;
  }

  if (typeof caption.raw?.text === "string") {
    return caption.raw.text;
  }

  if (Array.isArray(caption.raw?.captions) && caption.raw.captions.length > 0) {
    const firstCaption = caption.raw.captions[0];

    if (typeof firstCaption === "string") {
      return firstCaption;
    }

    return firstCaption?.caption || firstCaption?.text || "";
  }

  return "";
};

const normalizeCaptionOutputs = (items = [], type = "generated") => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      const text = getCaptionText(item);

      if (!text) {
        return null;
      }

      if (typeof item === "string") {
        return {
          caption: item,
          text: item,
          type,
        };
      }

      return {
        ...item,
        caption: text,
        text,
        type: item.type || type,
      };
    })
    .filter(Boolean);
};

const isVideoUrlForCaption = (url = "") => {
  return /\.(mp4|mov|webm)(\?|$)/i.test(String(url || "").toLowerCase());
};

const isVideoMediaItem = (item) => {
  return (
    item?.file?.type?.startsWith("video/") ||
    isVideoUrlForCaption(item?.imageUrl || item?.previewUrl || item?.name)
  );
};

const CaptionGeneratorApp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedGroup, groups } = useSelector((state) => state.groups);
  const { assets, status: libraryStatus } = useSelector((state) => state.library);

  const selectedGroupId = useMemo(() => {
    return groups.find((group) => group.name === selectedGroup)?._id;
  }, [groups, selectedGroup]);

  const [catalogLoading, setCatalogLoading] = useState(true);
  const [appConfig, setAppConfig] = useState(null);
  const [variantConfig, setVariantConfig] = useState(null);
  const [credits, setCredits] = useState(null);
  const [brandKit, setBrandKit] = useState(null);

  const [prompt, setPrompt] = useState("");
  const [caption, setCaption] = useState("");
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [selectedMedia, setSelectedMedia] = useState([]);

  const [generatedCaptions, setGeneratedCaptions] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [variantLoadingCaption, setVariantLoadingCaption] = useState(null);

  const hasVideoInput = selectedMedia.some(isVideoMediaItem);

  const captionUnitCost = hasVideoInput
    ? Number(appConfig?.cost?.videoCredits || 5)
    : Number(appConfig?.cost?.credits || 1);

  const requiredCredits = captionUnitCost * Number(options.n || 1);
  const remainingCredits = Number(credits?.remainingCredits || 0);
  const hasEnoughCredits = remainingCredits >= requiredCredits;

  const loadCatalog = async () => {
    setCatalogLoading(true);

    try {
      const response = await fetchAppsCatalog();

      const captionApp = (response.apps || []).find(
        (app) => app.appId === "caption.generate"
      );

      const captionVariantApp = (response.apps || []).find(
        (app) => app.appId === "caption.variant"
      );

      setAppConfig(captionApp || null);
      setVariantConfig(captionVariantApp || null);
      setCredits(response.credits || null);
    } catch (error) {
      message.error(error.message || "Failed to load Caption Generator.");
    } finally {
      setCatalogLoading(false);
    }
  };

  const loadBrandKit = async () => {
    if (!selectedGroupId) {
      setBrandKit(null);
      return;
    }

    try {
      const data = await getOnboardingForGroup(selectedGroupId);
      setBrandKit(data || null);

      if (data?.language) {
        setOptions((prev) => ({
          ...prev,
          language: data.language,
        }));
      }
    } catch (error) {
      setBrandKit(null);
    }
  };

  const refreshLibrary = () => {
    if (selectedGroupId) {
      dispatch(fetchAssets(selectedGroupId));
    }
  };

  const refreshCatalogCredits = async () => {
    try {
      const response = await fetchAppsCatalog();
      setCredits(response.credits || null);
    } catch (error) {
      console.warn("Failed to refresh app credits:", error.message);
    }
  };

  useEffect(() => {
    loadCatalog();
  }, []);

  useEffect(() => {
    if (selectedGroupId) {
      dispatch(fetchAssets(selectedGroupId));
      loadBrandKit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId, dispatch]);

  useEffect(() => {
    return () => {
      selectedMedia.forEach((item) => {
        if (item.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canGenerate = Boolean(
    selectedGroupId &&
      (prompt.trim() || caption.trim() || selectedMedia.length > 0) &&
      !isGenerating &&
      hasEnoughCredits
  );

  const buildPayload = () => {
    const uploadedFiles = selectedMedia
      .filter((item) => item.source === "upload" && item.file)
      .map((item) => item.file);

    const urls = selectedMedia
      .filter((item) => item.source !== "upload" && item.imageUrl)
      .map((item) => item.imageUrl);

    return {
      groupId: selectedGroupId,
      prompt: prompt.trim(),
      caption: caption.trim(),
      files: uploadedFiles,
      urls,
      useBrandKit: options.useBrandKit,
      options: {
        n: Number(options.n || 1),
        platform: options.platform,
        tone: options.tone,
        emoji_density: options.emoji_density,
        caption_length: options.caption_length,
        language: options.language,
        generate_hashtags: Boolean(options.generate_hashtags),
      },
    };
  };

  const handleGenerate = async () => {
    if (!selectedGroupId) {
      message.error("Please select a brand first.");
      return;
    }

    if (!prompt.trim() && !caption.trim() && selectedMedia.length === 0) {
      message.warning("Add a prompt, starting caption, image, or video.");
      return;
    }

    if (!hasEnoughCredits) {
      Modal.confirm({
        title: "Not enough credits",
        content: `Generating ${options.n} caption${
          Number(options.n) === 1 ? "" : "s"
        } needs ${requiredCredits} credit${
          requiredCredits === 1 ? "" : "s"
        }, but you only have ${remainingCredits}.`,
        okText: "Buy Add-ons",
        onOk: () => navigate("/Dashboard/AccountSetting"),
      });
      return;
    }

    setIsGenerating(true);
    setGeneratedCaptions([]);

    try {
      const response = await runCaptionGenerator(buildPayload());

      const captions = normalizeCaptionOutputs(
        response.captions || response.result?.captions || [],
        "generated"
      );

      setGeneratedCaptions(captions);
      setCredits(response.credits || credits);

      if (selectedGroupId) {
        refreshLibrary();
      }

      if (captions.length > 0) {
        message.success(
          `${captions.length} caption${captions.length === 1 ? "" : "s"} generated.`
        );
      } else {
        message.warning("The provider responded, but no readable captions were returned.");
      }
    } catch (error) {
      if (error.isLimitError) {
        Modal.confirm({
          title: "Not enough credits",
          content: error.message,
          okText: "Buy Add-ons",
          onOk: () => navigate("/Dashboard/AccountSetting"),
        });
      } else {
        message.error(error.message || "Failed to generate captions.");
      }
    } finally {
      setIsGenerating(false);
      refreshCatalogCredits();
    }
  };

  const handleVariantAction = async (captionText, type) => {
    if (!captionText) return;

    const variantCost = Number(variantConfig?.cost?.credits || 1);

    if (Number(credits?.remainingCredits || 0) < variantCost) {
      Modal.confirm({
        title: "Not enough credits",
        content: `This action needs ${variantCost} credit${
          variantCost === 1 ? "" : "s"
        }.`,
        okText: "Buy Add-ons",
        onOk: () => navigate("/Dashboard/AccountSetting"),
      });
      return;
    }

    setVariantLoadingCaption(captionText);

    try {
      const response = await runCaptionVariants({
        groupId: selectedGroupId,
        caption: captionText,
        expand: type === "expand",
        generate_similar: type === "similar",
        n: 1,
        useBrandKit: options.useBrandKit,
      });

      const variants = normalizeCaptionOutputs(
        response.variants ||
          response.result?.variants ||
          response.result?.captions ||
          [],
        type === "expand" ? "expanded" : "similar"
      );

      setGeneratedCaptions((prev) => [...variants, ...prev]);
      setCredits(response.credits || credits);

      if (variants.length > 0) {
        message.success("Caption variant generated.");
      } else {
        message.warning("The provider responded, but no readable variant was returned.");
      }
    } catch (error) {
      if (error.isLimitError) {
        Modal.confirm({
          title: "Not enough credits",
          content: error.message,
          okText: "Buy Add-ons",
          onOk: () => navigate("/Dashboard/AccountSetting"),
        });
      } else {
        message.error(error.message || "Failed to generate variant.");
      }
    } finally {
      setVariantLoadingCaption(null);
      refreshCatalogCredits();
    }
  };

  const handleUseCaption = async (captionText) => {
    try {
      await navigator.clipboard.writeText(captionText);
      message.success("Caption copied. You can paste it into Publisher.");
    } catch (error) {
      message.error("Could not copy caption.");
    }
  };

  if (catalogLoading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!appConfig) {
    return (
      <div className="h-screen bg-gray-50 p-6 overflow-hidden">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/Dashboard/Apps")}
        >
          Back to Apps
        </Button>

        <div className="bg-white rounded-3xl border p-12 mt-6">
          <Empty description="Caption Generator app is not available." />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 px-4 py-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto h-full flex flex-col">
        <header className="flex flex-wrap items-center justify-between gap-3 mb-3 flex-shrink-0">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/Dashboard/Apps")}
          >
            Back
          </Button>

          <Button
            type="text"
            icon={<HistoryOutlined />}
            onClick={() => navigate("/Dashboard/Apps/history")}
          >
            Activity
          </Button>
        </header>

        <section className="bg-white border border-gray-200 rounded-3xl shadow-sm overflow-hidden flex-1 min-h-0 flex flex-col">
          <div className="px-5 py-4 sm:px-6 border-b border-gray-100 bg-gradient-to-br from-white to-purple-50/50 flex-shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-purple-50 text-purple-700 ring-1 ring-purple-100 flex items-center justify-center">
                  <MessageOutlined className="text-xl" />
                </div>

                <div>
                  <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-gray-900 mb-0">
                    Caption Generator
                  </h1>

                  <p className="text-xs sm:text-sm text-gray-500 mb-0">
                    Generate captions from prompts, Brand Kit, images, videos, Media Library, or URLs.
                    Video captions use 5 credits each.
                  </p>
                </div>
              </div>

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-purple-100 px-3 py-1.5 text-xs sm:text-sm font-semibold text-purple-700 shadow-sm w-fit">
                <i className="fa-solid fa-bolt text-yellow-500"></i>
                {captionUnitCost} credit{captionUnitCost === 1 ? "" : "s"} / caption
              </div>
            </div>
          </div>

          {!selectedGroupId && (
            <div className="px-5 pt-4 sm:px-6 flex-shrink-0">
              <Alert
                type="warning"
                showIcon
                message="No brand selected"
                description="Please select a brand from the sidebar before using Caption Generator."
              />
            </div>
          )}

          <div className="p-4 sm:p-5 flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[390px_370px_1fr] gap-4 overflow-hidden">
            <aside className="min-h-0 overflow-hidden">
              <CaptionOptionsPanel
                prompt={prompt}
                setPrompt={setPrompt}
                caption={caption}
                setCaption={setCaption}
                options={options}
                setOptions={setOptions}
                brandKit={brandKit}
                appCost={{
                  ...appConfig.cost,
                  credits: captionUnitCost,
                  totalCredits: requiredCredits,
                  hasVideoInput,
                }}
              />
            </aside>

            <aside className="min-h-0 overflow-hidden">
              <CaptionMediaSelector
                assets={assets}
                libraryStatus={libraryStatus}
                selectedMedia={selectedMedia}
                setSelectedMedia={setSelectedMedia}
                onRefreshLibrary={refreshLibrary}
                maxItems={10}
              />
            </aside>

            <main className="min-h-0 flex flex-col">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3 flex-shrink-0">
                <div>
                  <h2 className="text-base font-extrabold text-gray-900 mb-0">
                    Generated Captions
                  </h2>
                  <p className="text-xs text-gray-500 mb-0">
                    Copy, expand, or generate similar variations.
                  </p>
                </div>

                <Button
                  type="primary"
                  icon={<ScissorOutlined />}
                  loading={isGenerating}
                  disabled={!canGenerate}
                  onClick={handleGenerate}
                >
                  Generate {options.n} · {requiredCredits} Credit
                  {requiredCredits === 1 ? "" : "s"}
                </Button>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3 flex-1 min-h-0 overflow-hidden">
                {isGenerating ? (
                  <div className="h-full flex flex-col items-center justify-center">
                    <Spin size="large" />
                    <p className="text-sm font-semibold text-gray-600 mt-3">
                      Generating captions...
                    </p>
                  </div>
                ) : generatedCaptions.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-center">
                    <Empty description="Your generated captions will appear here." />
                  </div>
                ) : (
                  <div className="h-full overflow-y-auto pr-1 space-y-3">
                    {generatedCaptions.map((item, index) => {
                      const text = getCaptionText(item);

                      return (
                        <CaptionResultCard
                          key={`${text}_${index}`}
                          caption={item}
                          index={index}
                          loadingVariant={variantLoadingCaption === text}
                          onExpand={(captionText) =>
                            handleVariantAction(captionText, "expand")
                          }
                          onSimilar={(captionText) =>
                            handleVariantAction(captionText, "similar")
                          }
                          onUse={handleUseCaption}
                        />
                      );
                    })}
                  </div>
                )}
              </div>

              {!hasEnoughCredits && requiredCredits > 0 && (
                <div className="mt-3 flex-shrink-0">
                  <Alert
                    type="error"
                    showIcon
                    message="Insufficient credits"
                    description={`You need ${requiredCredits} credits, but only have ${remainingCredits}.`}
                  />
                </div>
              )}
            </main>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CaptionGeneratorApp;